import { useState, type FormEvent } from 'react';

import {
    Badge,
    Button,
    Card,
    Field,
    Dropdown,
    Input,
    MonoLabel,
    Skeleton,
    StatusPill,
} from '@/design-system';
import {
    ADDABLE_DOMAIN_TYPES,
    DOMAIN_TYPE_LABELS,
    type DomainType,
    type TenantDomain,
} from '@/entities/tenant';
import { errorMessage, isApiError, problemKey } from '@/shared/api';
import { ErrorState } from '@/shared/ui';

import { domainsCopy, settingsCopy } from '../copy';
import { CopyInstructionButton } from './CopyInstructionButton';
import { DomainSetupPanel } from './DomainSetupPanel';
import {
    useAddDomain,
    useDomains,
    useMakePrimaryDomain,
    useRemoveDomain,
    useVerifyDomain,
} from '../hooks';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';
const MONO = 'font-[family-name:var(--font-mono)] text-[11px] break-all text-[var(--text-primary)]';

export interface DomainsCardProps {
    /** Toevoegen, controleren, hoofddomein kiezen en verwijderen: alleen de eigenaar. */
    canManage: boolean;
}

/**
 * Eigen domeinen (`/tenant/domains`). Een domein dat nog niet geverifieerd is,
 * zegt hier **wat er moet staan**: het TXT-record, of het bestand op de website.
 * "Verificatie mislukt" zonder die instructie is een raadsel. Eerst in gewone taal
 * wat het record doet, en een knop die de instructie met het record kopieert om
 * door te sturen naar wie de website beheert.
 *
 * Daarna volgt een rapportdomein zijn weg tot je rapportlinks erover lopen
 * ({@link DomainSetupPanel}): de stappen, wat er nu gebeurt of waarom het stopte,
 * en het CNAME-record dat het bedrijf zelf zet.
 */
export function DomainsCard({ canManage }: DomainsCardProps) {
    const domains = useDomains();

    return (
        <Card padding="lg" as="section" aria-labelledby="domains-title">
            <h2
                id="domains-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {domainsCopy.title}
            </h2>
            <p className={`m-0 mt-1 mb-[10px] ${SMALL}`}>{domainsCopy.intro}</p>

            {domains.isPending ? (
                <div aria-busy="true">
                    <Skeleton height={14} label={`${domainsCopy.title} laden`} />
                    <Skeleton width="70%" height={14} className="mt-2" />
                </div>
            ) : domains.isError ? (
                <ErrorState
                    error={domains.error}
                    title={domainsCopy.title}
                    onRetry={() => void domains.refetch()}
                />
            ) : domains.data.length === 0 ? (
                <p className="m-0 py-[9px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {domainsCopy.empty}
                </p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {domains.data.map((domain) => (
                        <DomainRow key={domain.uuid} domain={domain} canManage={canManage} />
                    ))}
                </ul>
            )}

            {canManage ? (
                <AddDomainForm />
            ) : (
                <p className={`m-0 mt-2 ${SMALL}`}>{settingsCopy.readOnly}</p>
            )}
        </Card>
    );
}

function DomainRow({ domain, canManage }: { domain: TenantDomain; canManage: boolean }) {
    const [confirming, setConfirming] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const verify = useVerifyDomain();
    const primary = useMakePrimaryDomain();
    const remove = useRemoveDomain();
    const busy = verify.isPending || primary.isPending || remove.isPending;
    const error = verify.error ?? primary.error ?? remove.error;

    function reset() {
        setNotice(null);
        verify.reset();
        primary.reset();
        remove.reset();
    }

    return (
        <li className="border-b border-[var(--border-subtle-2)] py-[11px]">
            <div className="flex flex-wrap items-center gap-[10px]">
                <span className="min-w-0 flex-1 truncate text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                    {domain.hostname}
                </span>
                {domain.primary ? (
                    <Badge variant="text" tone="accent">
                        {domainsCopy.primary}
                    </Badge>
                ) : null}
                <StatusPill tone={domain.verified ? 'accent' : 'warn'} size="sm">
                    {domain.verified ? domainsCopy.verified : domainsCopy.pending}
                </StatusPill>
            </div>
            <p className={`m-0 mt-[2px] ${SMALL}`}>
                {`${DOMAIN_TYPE_LABELS[domain.type]} · ${domainsCopy.certificate(domain.sslStatusLabel)}`}
            </p>

            {!domain.verified && domain.verification ? (
                <div className="mt-[10px] rounded-[7px] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-[10px]">
                    <p className="m-0 text-[12px] leading-[1.45] text-[var(--text-secondary)]">
                        {domainsCopy.instructions}
                    </p>

                    <MonoLabel size={10} tone="muted" className="mt-2 block">
                        {domainsCopy.txtTitle}
                    </MonoLabel>
                    <dl className="m-0 mt-[4px] grid grid-cols-[64px_minmax(0,1fr)] gap-x-[10px] gap-y-[3px]">
                        <dt className="text-[12px] text-[var(--text-secondary)]">
                            {domainsCopy.txtName}
                        </dt>
                        <dd className={`m-0 ${MONO}`}>{domain.verification.txtName}</dd>
                        <dt className="text-[12px] text-[var(--text-secondary)]">
                            {domainsCopy.txtValue}
                        </dt>
                        <dd className={`m-0 ${MONO}`}>{domain.verification.txtRecord}</dd>
                    </dl>
                    <p className="m-0 mt-[6px] text-[11px] leading-[1.45] text-[var(--text-secondary)]">
                        {domainsCopy.txtNameHint}
                    </p>
                    <CopyInstructionButton
                        text={domainsCopy.txtInstruction(
                            domain.hostname,
                            domain.verification.txtName,
                            domain.verification.txtRecord,
                        )}
                    />

                    <MonoLabel size={10} tone="muted" className="mt-2 block">
                        {domainsCopy.fileTitle}
                    </MonoLabel>
                    <dl className="m-0 mt-[4px] grid grid-cols-[64px_minmax(0,1fr)] gap-x-[10px] gap-y-[3px]">
                        <dt className="text-[12px] text-[var(--text-secondary)]">
                            {domainsCopy.fileAddress}
                        </dt>
                        <dd className={`m-0 ${MONO}`}>
                            {`https://${domain.hostname}${domain.verification.wellKnownPath}`}
                        </dd>
                        <dt className="text-[12px] text-[var(--text-secondary)]">
                            {domainsCopy.fileContent}
                        </dt>
                        <dd className={`m-0 ${MONO}`}>{domain.verification.token}</dd>
                    </dl>
                </div>
            ) : null}

            {domain.verified && domain.type === 'report' && domain.setup.step !== 'active' ? (
                <DomainSetupPanel domain={domain} canManage={canManage} />
            ) : null}

            {canManage && confirming ? (
                <Card
                    variant="warn"
                    padding="md"
                    radius={9}
                    className="mt-[10px]"
                    role="group"
                    aria-label={domainsCopy.removeLabel(domain.hostname)}
                >
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                        {domainsCopy.removeBody(domain.hostname)}
                    </p>
                    <div className="mt-[10px] flex flex-wrap gap-2">
                        <Button
                            variant="warn"
                            size="sm"
                            disabled={remove.isPending}
                            onClick={() =>
                                remove.mutate(domain.uuid, {
                                    onSuccess: () => setConfirming(false),
                                })
                            }
                        >
                            {domainsCopy.removeYes}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                            {settingsCopy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : null}

            {canManage && !confirming ? (
                <div className="mt-[10px] flex flex-wrap gap-2">
                    {domain.verified ? null : (
                        <Button
                            variant="outline"
                            size="xs"
                            disabled={busy}
                            onClick={() => {
                                reset();
                                verify.mutate(domain.uuid, {
                                    onSuccess: (checked) =>
                                        setNotice(
                                            checked.verified
                                                ? domainsCopy.verifiedNow(checked.hostname)
                                                : null,
                                        ),
                                });
                            }}
                        >
                            {verify.isPending ? domainsCopy.verifying : domainsCopy.verify}
                        </Button>
                    )}
                    {domain.verified && !domain.primary ? (
                        <Button
                            variant="outline"
                            size="xs"
                            disabled={busy}
                            onClick={() => {
                                reset();
                                primary.mutate(domain.uuid, {
                                    onSuccess: () =>
                                        setNotice(domainsCopy.madePrimary(domain.hostname)),
                                });
                            }}
                        >
                            {domainsCopy.makePrimary}
                        </Button>
                    ) : null}
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={busy}
                        onClick={() => {
                            reset();
                            setConfirming(true);
                        }}
                    >
                        {domainsCopy.remove}
                    </Button>
                </div>
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {error ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.45] text-[var(--warn-text)]"
                >
                    {domainError(error, domain.hostname)}
                </p>
            ) : null}
        </li>
    );
}

/** Een mislukte controle is een instructie, geen fout: amber, met wat er wél stond. */
function domainError(error: unknown, hostname: string): string {
    if (problemKey(error) !== 'domain-verification-failed' || !isApiError(error)) {
        return errorMessage(error);
    }

    const observed = Array.isArray(error.problem.observed)
        ? error.problem.observed.filter((value): value is string => typeof value === 'string')
        : [];

    return observed.length > 0
        ? `${domainsCopy.verifyFailed(hostname)} ${domainsCopy.observed(observed.join(', '))}`
        : domainsCopy.verifyFailed(hostname);
}

function AddDomainForm() {
    const add = useAddDomain();
    const [hostname, setHostname] = useState('');
    const [type, setType] = useState<DomainType>('report');
    const [required, setRequired] = useState(false);

    const fieldError = (field: string) =>
        isApiError(add.error) ? add.error.firstValidationError(field) : undefined;
    const formError =
        add.error && !fieldError('hostname') && !fieldError('type')
            ? errorMessage(add.error)
            : null;

    function submit(event: FormEvent) {
        event.preventDefault();

        if (hostname.trim() === '') {
            setRequired(true);

            return;
        }

        setRequired(false);
        add.mutate({ hostname: hostname.trim(), type }, { onSuccess: () => setHostname('') });
    }

    return (
        <form
            onSubmit={submit}
            noValidate
            aria-labelledby="add-domain-title"
            className="mt-[14px] border-t border-[var(--border-subtle)] pt-[14px]"
        >
            <h3
                id="add-domain-title"
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {domainsCopy.addTitle}
            </h3>

            <div className="flex flex-wrap items-start gap-2">
                <Field
                    label={domainsCopy.hostname}
                    size="sm"
                    className="min-w-[200px] flex-1"
                    error={required ? domainsCopy.hostnameRequired : fieldError('hostname')}
                >
                    <Input
                        value={hostname}
                        placeholder={domainsCopy.hostnamePlaceholder}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) => setHostname(event.target.value)}
                    />
                </Field>
                <Field
                    label={domainsCopy.type}
                    size="sm"
                    className="min-w-[150px]"
                    error={fieldError('type')}
                >
                    <Dropdown
                        value={type}
                        options={ADDABLE_DOMAIN_TYPES.map((option) => ({
                            value: option,
                            label: DOMAIN_TYPE_LABELS[option],
                        }))}
                        onChange={setType}
                    />
                </Field>
                <div className="flex min-h-[64px] items-end">
                    <Button type="submit" variant="outline" size="sm" disabled={add.isPending}>
                        {add.isPending ? domainsCopy.adding : domainsCopy.add}
                    </Button>
                </div>
            </div>

            {add.isSuccess ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {domainsCopy.added(add.data.hostname)}
                </p>
            ) : null}

            {formError ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {formError}
                </p>
            ) : null}
        </form>
    );
}
